import instance from "../axiosConfig";
import {
    CreateElementPage,
    CreateElementPageConfiguration,
    DatabaseElement,
    ElementPage,
    ElementPageConfiguration,
    GidData,
    IfcData,
} from "./models";

export class ElementDatabaseService {
    private regionCode: string;
    private languageCode: string;

    constructor(regionCode: string, languageCode: string) {
        this.regionCode = regionCode;
        this.languageCode = languageCode;
    }

    get = async (): Promise<DatabaseElement[]> => {
        const response = await instance.get<DatabaseElement[]>(
            `api/admin/${this.regionCode}/languages/${this.languageCode}/elementoverview`
        );

        return response.data.sort((a, b) => (a.code ?? "0").localeCompare(b.code));
    };

    getElementPageConfiguration = async (elementId: string): Promise<ElementPageConfiguration | null> => {
        const response = await instance.get<ElementPageConfiguration>(
            `api/admin/${this.regionCode}/elementpages/${elementId}`
        );

        if (response.status === 404) return null;

        return response.data;
    };

    createElementPageConfiguration = async (
        elementId: string,
        body: CreateElementPageConfiguration
    ): Promise<ElementPageConfiguration> => {
        const response = await instance.post<ElementPageConfiguration>(
            `api/admin/${this.regionCode}/elementpages/${elementId}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    updateElementPageConfiguration = async (
        elementId: string,
        body: CreateElementPageConfiguration
    ): Promise<ElementPageConfiguration> => {
        const response = await instance.put<ElementPageConfiguration>(
            `api/admin/${this.regionCode}/elementpages/${elementId}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    getElementPage = async (elementId: string, languageCode: string): Promise<ElementPage | undefined> => {
        const response = await instance.get<ElementPage>(
            `api/admin/${this.regionCode}/elementpages/${elementId}/language/${languageCode}`
        );

        if (response.status >= 200 && response.status < 300) return response.data;
    };

    storeElementPage = async (
        elementId: string,
        languageCode: string,
        body: CreateElementPage
    ): Promise<ElementPage> => {
        const response = await instance.post<ElementPage>(
            `api/admin/${this.regionCode}/elementpages/${elementId}/language/${languageCode}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        return response.data;
    };

    getElementMatrix = async (elementId: string, languageCode: string): Promise<GidData> => {
        const response = await instance.get<GidData>(
            `api/admin/${this.regionCode}/elementpages/${elementId}/language/${languageCode}/table/LOI`
        );

        return response.data;
    };

    getIfcMatrix = async (elementId: string, languageCode: string): Promise<IfcData> => {
        const response = await instance.get<IfcData>(
            `api/admin/${this.regionCode}/elementpages/${elementId}/language/${languageCode}/table/IFC`
        );

        return response.data;
    };

    synchroniseInformationTables = async (
        ignoreChanges: boolean,
        ignoreFootnotes: boolean,
        ignoreVisibility: boolean
    ) => {
        const response = await instance.post(`api/admin/${this.regionCode}/synchronizeallpages`, {
            keepFootnotes: ignoreFootnotes,
            keepHidden: ignoreVisibility,
            keepoverride: ignoreChanges,
        });

        return response.status === 200 || response.status === 204;
    };
}
